import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { TableV2Footer } from '@/features/shared/components/tableSystem/TableV2Footer';
import {
  tableV2FrameSx,
  tableV2PanelSx,
  tableV2RootSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import {
  buildTableV2ResetKey,
  formatTableV2DisplayedRowsLabel,
  resolveTableV2FillerRowCount,
  resolveTableV2PageSize,
  resolveTableV2PageSizeOptions,
  resolveTableV2SkeletonRowCount,
  shouldFillTableV2Rows,
  shouldShowTableV2SkeletonRows,
  shouldShowTableV2PaginationControls,
} from '@/features/shared/components/tableSystem/TableV2QueryShellSupport';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import {
  renderTableV2FillerRows,
  renderTableV2StateRows,
} from '@/features/shared/components/tableSystem/TableV2StateRows';
import { useForcedLoadingState } from '@/shared/hooks/useForcedLoadingState';
import {
  Box,
  DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
  Surface,
  Table,
  TableBody,
  TableHead,
} from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { QueryResult } from '@flowqa/shared';

type TableV2DataState = 'empty' | 'error' | 'loading' | 'ready';

export type TableV2QueryShellProps<TRow> = {
  active?: boolean;
  ariaLabel: string;
  colgroup: ReactNode;
  emptyText: string;
  entityLabel: string;
  entitySingularLabel?: string;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  header: ReactNode;
  hideFooter?: boolean;
  initialLoadingFallback?: ReactNode;
  initialPageSize?: number;
  minimumVisibleRows?: number;
  pageSizeOptions?: number[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TRow>>;
  queryKey: QueryKey;
  requireAuth?: boolean;
  renderRow: (row: TRow, context: { isLastVisibleRow: boolean }) => ReactNode;
  search?: string;
  skeletonColumns: TableV2SkeletonColumnKind[];
  skeletonRowCount?: number;
  showLastSkeletonDivider?: boolean;
  sorts: GridQueryInput['sorts'];
  sx?: SxProps<Theme>;
  onQueryMeta?: (meta: { total: number; items: TRow[] } | null) => void;
};

const buildQueryInput = ({
  fixedFilters,
  page,
  pageSize,
  search,
  sorts,
}: {
  fixedFilters?: GridQueryInput['filters'];
  page: number;
  pageSize: number;
  search?: string;
  sorts: GridQueryInput['sorts'];
}): GridQueryInput => ({
  page: page + 1,
  pageSize,
  sorts,
  filters: fixedFilters ?? [],
  search: search?.trim() ? search.trim() : undefined,
});

export const TableV2QueryShell = <TRow extends { id: string }>({
  active = true,
  ariaLabel,
  colgroup,
  entityLabel,
  entitySingularLabel,
  fixedFilters,
  forceLoading = false,
  header,
  hideFooter = false,
  initialLoadingFallback,
  initialPageSize = 10,
  minimumVisibleRows,
  onQueryMeta,
  pageSizeOptions = [10, 25, 50],
  queryFn,
  queryKey,
  requireAuth = true,
  renderRow,
  search,
  skeletonColumns,
  skeletonRowCount: skeletonRowCountOverride,
  showLastSkeletonDivider = false,
  sorts,
  sx,
}: TableV2QueryShellProps<TRow>): ReactElement => {
  const { session, loading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const showForcedLoadingState = useForcedLoadingState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => resolveTableV2PageSize(initialPageSize));
  const resolvedMinimumVisibleRows = minimumVisibleRows ?? pageSize;
  const resolvedPageSizeOptions = useMemo(
    () => resolveTableV2PageSizeOptions(pageSizeOptions),
    [pageSizeOptions],
  );
  const resetKey = useMemo(
    () => buildTableV2ResetKey({ fixedFilters, queryKey, search, sorts }),
    [fixedFilters, queryKey, search, sorts],
  );
  const queryInput = useMemo(
    () => buildQueryInput({ fixedFilters, page, pageSize, search, sorts }),
    [fixedFilters, page, pageSize, search, sorts],
  );
  const queryInputKey = useMemo(() => JSON.stringify(queryInput), [queryInput]);
  const resolvedQueryKey = useMemo<QueryKey>(
    () => [
      ...queryKey,
      'v2',
      selectedOrganisationId ?? 'personal',
      selectedProjectId ?? 'all',
      queryInputKey,
    ],
    [queryInputKey, queryKey, selectedOrganisationId, selectedProjectId],
  );
  const rowsQuery = useQuery({
    queryKey: resolvedQueryKey,
    enabled: (requireAuth ? session !== null : true) && active,
    queryFn: async () => await queryFn(queryInput),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });
  const rows = rowsQuery.data?.items ?? [];
  const hasRowsData = rowsQuery.data !== undefined;
  const total = rowsQuery.data?.total ?? 0;
  const displayedFrom = total > 0 ? page * pageSize + 1 : 0;
  const displayedTo = total > 0 ? Math.min(total, (page + 1) * pageSize) : 0;
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const bodyContentRowCount = rows.length > 0 ? rows.length : 1;
  const columnCount = skeletonColumns.length;
  const showSkeletonRows = shouldShowTableV2SkeletonRows({
    authLoading: requireAuth && loading,
    forceLoading: forceLoading || showForcedLoadingState,
    hasData: hasRowsData,
    placeholderData: rowsQuery.isPlaceholderData,
    queryFetching: rowsQuery.isFetching,
    queryLoading: rowsQuery.isLoading,
  });
  const dataState: TableV2DataState = rowsQuery.isError
    ? 'error'
    : showSkeletonRows
      ? 'loading'
      : rows.length === 0
        ? 'empty'
        : 'ready';
  const dataError =
    rowsQuery.error instanceof Error ? rowsQuery.error.message : 'Table request failed';
  const shouldFillRows = shouldFillTableV2Rows(total, pageSize);
  const skeletonRowCount =
    skeletonRowCountOverride ??
    resolveTableV2SkeletonRowCount({
      minimumVisibleRows: resolvedMinimumVisibleRows,
      pageSize,
      placeholderData: rowsQuery.isPlaceholderData,
      rowCount: rows.length,
    });
  const renderedBodyContentRowCount = showSkeletonRows ? skeletonRowCount : bodyContentRowCount;
  const fillerRowCount = resolveTableV2FillerRowCount({
    bodyContentRowCount: renderedBodyContentRowCount,
    minimumVisibleRows: resolvedMinimumVisibleRows,
    placeholderData: rowsQuery.isPlaceholderData,
    shouldFillRows,
    showSkeletonRows,
  });
  const displayedRowsLabel = formatTableV2DisplayedRowsLabel({
    displayedFrom,
    displayedTo,
    entityLabel,
    entitySingularLabel,
    pageSize,
    total,
  });
  const shouldRenderStateRows = !showSkeletonRows && (rowsQuery.isError || rows.length === 0);
  const stateText = rowsQuery.isError ? dataError : DATA_GRID_EMPTY_STATE_NO_DATA_LABEL;
  const stateRows = shouldRenderStateRows
    ? renderTableV2StateRows({ ariaLabel, columnCount, stateText })
    : null;
  const dataRows =
    !showSkeletonRows && !shouldRenderStateRows
      ? rows.map(
          (row, index): ReactNode =>
            renderRow(row, {
              isLastVisibleRow: fillerRowCount === 0 && index === rows.length - 1,
            }),
        )
      : null;
  const bodyRows = showSkeletonRows
    ? Array.from({ length: skeletonRowCount }, (_item, index) => (
        <TableV2SkeletonRow
          key={`${ariaLabel}-skeleton-${String(index)}`}
          columns={skeletonColumns}
          isLastVisibleRow={fillerRowCount === 0 && index === skeletonRowCount - 1}
          rowIndex={index}
          showLastDivider={showLastSkeletonDivider}
        />
      ))
    : (stateRows ?? dataRows);

  useEffect(() => {
    if (!active) return;
    setPage(0);
  }, [active, resetKey]);

  useEffect(() => {
    if (!active) return;
    onQueryMeta?.(
      rowsQuery.data ? { total: rowsQuery.data.total, items: rowsQuery.data.items } : null,
    );
  }, [active, onQueryMeta, rowsQuery.data]);

  const handlePageChange = (nextPage: number): void => {
    if (nextPage < 0 || nextPage >= pageCount || nextPage === page) return;
    setPage(nextPage);
  };

  if (showSkeletonRows && !hasRowsData && initialLoadingFallback) {
    return <>{initialLoadingFallback}</>;
  }

  return (
    <Surface
      padding={null}
      radius="card"
      background="primary"
      sx={[tableV2FrameSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}
    >
      <Box
        data-testid="table-v2-ready"
        data-state={dataState}
        data-error={dataState === 'error' ? dataError : ''}
        data-fetching={rowsQuery.isFetching ? 'true' : 'false'}
        data-row-count={String(rows.length)}
        data-total={String(total)}
        sx={{ display: 'none' }}
      />
      <Box sx={tableV2PanelSx}>
        <Table aria-label={ariaLabel} data-testid="table-v2-root" sx={tableV2RootSx}>
          {colgroup}
          <TableHead>{header}</TableHead>
          <TableBody>
            {bodyRows}
            {renderTableV2FillerRows({ ariaLabel, columnCount, fillerRowCount })}
          </TableBody>
        </Table>
      </Box>
      {hideFooter ? null : (
        <TableV2Footer
          label={displayedRowsLabel}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={resolvedPageSizeOptions}
          showControls={shouldShowTableV2PaginationControls(total, pageSize)}
          onPageChange={handlePageChange}
          onPageSizeChange={(nextPageSize) => {
            setPage(0);
            setPageSize(resolveTableV2PageSize(nextPageSize));
          }}
        />
      )}
    </Surface>
  );
};
